"use client";

import { useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, useInView } from "framer-motion";
import { Zap, ShieldCheck, Layers, Brain, ArrowUpRight } from "lucide-react";

/* ── animation helpers ─────────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay,
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1, delayChildren: 0.15 } },
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  },
};

/* ── data ──────────────────────────────────────────────────── */
const values = [
  {
    icon: Zap,
    title: "Speed without shortcuts",
    body: "Every product we ship is fast by design — optimised at the code level, not patched after the fact.",
  },
  {
    icon: ShieldCheck,
    title: "Security by default",
    body: "Auth, data handling, and deployments are built with security as a first-class concern, not an afterthought.",
  },
  {
    icon: Layers,
    title: "Full ownership",
    body: "We don't hand off half-finished work. From design to deployment, every layer is our responsibility.",
  },
  {
    icon: Brain,
    title: "Built for the future",
    body: "Actively growing into blockchain and AI-driven applications — building solutions that are intelligent today and ready for what's next.",
  },
];

const stack = [
  "Next.js", "TypeScript", "React", "Tailwind CSS",
  "Node.js", "PostgreSQL", "MongoDB", "Prisma",
  "React Native", "Expo", "Swift", "Kotlin",
  "Rust", "Go", "Java", "Docker",
  "Nginx", "Caddy", "GitHub Actions", "Vercel",
];

/* ── section wrapper with scroll-trigger ───────────────────── */
function InViewSection({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      variants={stagger}
      initial="hidden"
      animate={isInView ? "show" : "hidden"}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ── main component ─────────────────────────────────────────── */
export default function AboutContent() {
  return (
    <section className="mx-auto max-w-6xl px-4 pb-24 pt-10 sm:px-8 sm:pb-32">

      {/* ── 1. ENTITY ────────────────────────────────────────── */}
      <InViewSection className="mb-24 grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
        <div className="flex flex-col gap-6">
          <div className="space-y-4">
            <motion.p variants={staggerItem} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[var(--accent)]">
              <span className="h-1 w-1 rounded-full bg-[var(--accent)]"></span>
              The Studio
            </motion.p>
            <motion.h2 variants={staggerItem} className="text-4xl font-extrabold tracking-tighter text-[var(--foreground)] md:text-5xl lg:whitespace-nowrap">
              What is Dev By Skillz?
            </motion.h2>
          </div>
          
          <div className="mt-4 space-y-6 text-base leading-relaxed text-[var(--foreground-muted)]">
            <motion.p variants={staggerItem}>
              Dev By Skillz is a full-stack digital studio with one core belief: great software should be fast, reliable, and built to last. We work with businesses and founders who need more than code — they need a technical partner who understands product, design, and delivery from end to end.
            </motion.p>
            <motion.p variants={staggerItem}>
              From the first wireframe to production deployment, we own every layer. Whether it&rsquo;s a high-converting website, a native mobile app, a robust backend API, or a desktop application — the standard doesn&rsquo;t change.
            </motion.p>
            <motion.p variants={staggerItem}>
              Our vision for the future is to expand into blockchain and AI-driven applications — helping businesses build digital products that are not just modern today, but highly secure and intelligent for what&rsquo;s coming next.
            </motion.p>
          </div>
        </div>

        {/* Values grid */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:mt-16">
          {values.map((v) => {
            const Icon = v.icon;
            return (
              <motion.div
                key={v.title}
                variants={staggerItem}
                className="group flex flex-col gap-4 rounded-3xl border border-[var(--border)] bg-[var(--background)] p-6 shadow-sm transition-all hover:border-[var(--accent)]/30 hover:shadow-md hover:shadow-[var(--accent)]/5"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--accent)]/10 transition-colors group-hover:bg-[var(--accent)] group-hover:text-white text-[var(--accent)]">
                  <Icon size={22} className="transition-transform group-hover:scale-110" />
                </div>
                <div>
                  <p className="mb-2 text-base font-bold text-[var(--foreground)]">{v.title}</p>
                  <p className="text-sm leading-relaxed text-[var(--foreground-muted)]">{v.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </InViewSection>
      {/* ── divider ──────────────────────────────────────────── */}
      <div className="mb-24 h-px w-full bg-gradient-to-r from-transparent via-[var(--border)] to-transparent" />

      {/* ── 2. PERSON ────────────────────────────────────────── */}
      <InViewSection className="grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-20">

        {/* Bio + stack */}
        <div className="flex flex-col gap-8 order-2 lg:order-1">
          <div className="space-y-4">
            <motion.p variants={staggerItem} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[var(--accent)]">
              <span className="h-1 w-1 rounded-full bg-[var(--accent)]"></span>
              The Person
            </motion.p>
            <motion.h2 variants={staggerItem} className="text-4xl font-extrabold tracking-tighter text-[var(--foreground)] md:text-5xl lg:whitespace-nowrap">
              The builder behind the brand
            </motion.h2>
          </div>

          <div className="mt-2 space-y-6 text-base leading-relaxed text-[var(--foreground-muted)]">
            <motion.p variants={staggerItem}>
              Dev By Skillz is the engineer behind the studio — a full-stack developer fluent across the entire product stack. Whether it&rsquo;s crafting pixel-perfect frontends, architecting backend systems, building native iOS apps in Swift or Android apps in Kotlin, writing high-performance desktop software in Rust, Go, or Java, or wiring up infrastructure with reverse proxies and CI/CD pipelines — the work varies; the standard doesn&rsquo;t.
            </motion.p>
            <motion.p variants={staggerItem}>
              This isn&rsquo;t a generalist who dabbles in everything. It&rsquo;s years of deliberate depth across every layer of the stack — built out of genuine curiosity and a commitment to understanding how things actually work, not just how to use them.
            </motion.p>
          </div>

          {/* Stack tags */}
          <motion.div variants={stagger} className="mt-4 flex flex-wrap gap-2.5">
            {stack.map((tag) => (
              <motion.span
                key={tag}
                variants={staggerItem}
                className="rounded-full border border-[var(--border)] bg-[var(--foreground)]/[0.02] px-4 py-1.5 text-sm font-medium text-[var(--foreground)] backdrop-blur-sm transition-colors hover:border-[var(--accent)] hover:bg-[var(--accent)]/5 hover:text-[var(--accent)]"
              >
                {tag}
              </motion.span>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div variants={staggerItem} className="mt-6">
            <Link
              href="/work"
              className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full bg-[var(--accent)] px-8 py-4 text-sm font-bold text-white shadow-lg shadow-[var(--accent)]/20 transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-[var(--accent)]/30 active:translate-y-0"
            >
              <span className="relative z-10">See the work</span>
              <div className="relative z-10 flex h-6 w-6 items-center justify-center rounded-full bg-white/20 transition-transform group-hover:rotate-45">
                <ArrowUpRight size={14} className="text-white" />
              </div>
              <div className="absolute inset-0 z-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 opacity-0 transition-opacity duration-500 group-hover:animate-shimmer group-hover:opacity-100" />
            </Link>
          </motion.div>
        </div>

        {/* Avatar + name card */}
        <motion.div variants={staggerItem} className="flex flex-col items-center gap-6 lg:items-end order-1 lg:order-2 z-10 relative">
          <div className="group relative flex aspect-square w-full max-w-[320px] items-center justify-center overflow-hidden rounded-3xl bg-[var(--accent)]/5 sm:max-w-full">
            <Image 
              src="https://api.dicebear.com/9.x/toon-head/svg?seed=Alexander"
              alt="Developer behind Dev By Skillz"
              fill
              unoptimized
              className="object-cover transition-transform duration-[1.5s] ease-out group-hover:scale-105"
            />
            
            <div className="absolute inset-0 rounded-3xl border-2 border-[var(--accent)]/10 transition-colors group-hover:border-[var(--accent)]/30 z-10" />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[var(--background)]/90 via-[var(--background)]/20 to-transparent z-10" />
            
            {/* Ambient Background Glow */}
            <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 h-[200%] w-[200%] -translate-x-1/2 -translate-y-1/2 bg-[radial-gradient(ellipse_at_center,var(--accent)_0%,transparent_50%)] opacity-20 mix-blend-color-dodge transition-opacity duration-700 group-hover:opacity-40" />
            
            {/* Info overlay */}
            <div className="absolute bottom-6 left-6 right-6 z-20 flex items-center justify-between rounded-2xl border border-[var(--border)] bg-[var(--background)]/80 p-4 backdrop-blur-md transition-transform duration-500 group-hover:-translate-y-2">
              <div>
                <p className="text-lg font-extrabold text-[var(--foreground)]">Dev By Skillz</p>
                <p className="text-sm font-medium text-[var(--accent)]">Full-Stack Engineer</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--foreground)] text-[var(--background)]">
                <Brain size={18} />
              </div>
            </div>
          </div>
        </motion.div>
      </InViewSection>
    </section>
  );
}
